import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaClipboardList, 
  FaFilter, 
  FaEye, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaExclamationCircle, 
  FaShoppingCart,
  FaCalendarAlt,
  FaFileAlt,
  FaInfoCircle,
  FaBuilding,
  FaSearch,
  FaClipboardCheck,
  FaPrint,
  FaTag,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import supabase from '../../supabaseClient';
import '../../styles/order-page.css';
import { useAuth } from '../../hooks/useAuth';
import { printImageBasedReceipt } from './imageBasedReceiptUtils';

const OrdersList = () => {
  // Estado para armazenar dados
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  
  // Estados específicos para admin
  const [expositores, setExpositores] = useState([]);
  const [expositorFilter, setExpositorFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');

  // Novos estados para filtros melhorados
  const [eventos, setEventos] = useState([]);
  const [eventoFilter, setEventoFilter] = useState('Todos');
  const [dateFilter, setDateFilter] = useState('Todos');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [valueFilter, setValueFilter] = useState('Todos');
  const [showFilters, setShowFilters] = useState(false);

  // Estados para as abas do modal
  const [activeTab, setActiveTab] = useState('resumo');
  const [orderSummary, setOrderSummary] = useState({
    originalTotal: 0,
    discountTotal: 0,
    finalTotal: 0,
    itemCount: 0,
    itemsWithDiscount: 0
  });

  // Cores e estilo consistentes com o design system
  const colors = {
    primary: '#0078D7',
    primaryLight: '#E3F2FD',
    primaryDark: '#005A9E',
    secondary: '#505050',
    success: '#28A745',
    successLight: '#D4EDDA',
    danger: '#DC3545',
    dangerLight: '#F8D7DA',
    warning: '#FFC107',
    warningLight: '#FFF3CD',
    info: '#17A2B8',
    infoLight: '#D1ECF1',
    light: '#F8F9FA',
    dark: '#343A40',
    muted: '#6C757D',
    white: '#FFFFFF',
    border: '#DEE2E6',
    shadow: 'rgba(0, 0, 0, 0.1)'
  };

  // Estilos para o modal flutuante (mantidos do original)
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '1000px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.white,
      borderRadius: '12px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: 'none',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.border} 100%)`,
      borderBottom: `1px solid ${colors.border}`,
      padding: '1.25rem 1.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: colors.white,
      color: colors.muted,
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: `0 2px 5px ${colors.shadow}`
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: colors.dark,
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    tabsContainer: {
      display: 'flex',
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.light,
      padding: '0 1.5rem',
      flexShrink: 0
    },
    tab: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1rem 1.5rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: colors.muted,
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s ease',
      position: 'relative',
      backgroundColor: 'transparent'
    },
    activeTab: {
      color: colors.primary,
      borderBottomColor: colors.primary,
      backgroundColor: colors.white,
      fontWeight: '600'
    },
    tabContent: {
      padding: '0',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.25rem 1.5rem',
      borderTop: `1px solid ${colors.border}`,
      background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.border} 100%)`,
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    cancelButton: {
      border: `1px solid ${colors.border}`,
      background: colors.white,
      color: colors.secondary,
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: colors.dark,
      color: colors.white,
      boxShadow: `0 4px 10px ${colors.shadow}`
    },
    section: {
      margin: '0',
      padding: '1.5rem',
      borderBottom: `1px solid ${colors.border}`
    },
    sectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: colors.dark,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem'
    },
    infoLabel: {
      fontSize: '0.8rem',
      color: colors.muted,
      marginBottom: '0.25rem'
    },
    infoValue: {
      fontSize: '0.95rem',
      fontWeight: '500',
      color: colors.dark
    },
    itemTable: {
      width: '100%',
      borderCollapse: 'collapse',
      background: colors.white
    },
    itemTableHeader: {
      backgroundColor: colors.light,
      color: colors.secondary,
      fontSize: '0.85rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '1rem',
      textAlign: 'left',
      borderBottom: `1px solid ${colors.border}`,
      position: 'sticky',
      top: 0,
      zIndex: 2
    },
    itemTableCell: {
      padding: '1rem',
      borderBottom: `1px solid ${colors.border}`,
      fontSize: '0.95rem',
      color: colors.dark,
      verticalAlign: 'middle'
    },
    discountBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem 0.65rem',
      borderRadius: '50px',
      fontSize: '0.75rem',
      fontWeight: '600',
      backgroundColor: colors.successLight,
      color: colors.success
    },
    priceContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end'
    },
    originalPrice: {
      textDecoration: 'line-through',
      color: colors.muted,
      fontSize: '0.85rem'
    },
    discountedPrice: {
      color: colors.success,
      fontWeight: '600'
    },
    regularPrice: {
      fontWeight: '500'
    },
    orderSummaryCard: {
      backgroundColor: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: `0 4px 10px ${colors.shadow}`,
      marginBottom: '1.5rem'
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.75rem'
    },
    summaryLabel: {
      fontSize: '0.95rem',
      color: colors.secondary
    },
    summaryValue: {
      fontSize: '1rem',
      fontWeight: '600',
      color: colors.dark
    },
    summaryTotal: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: colors.primary,
      padding: '0.75rem 0',
      borderTop: `1px solid ${colors.border}`,
      marginTop: '0.5rem'
    },
    discountTotal: {
      color: colors.success
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.4rem 0.8rem',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  };

  // Configuração das abas
  const tabs = [
    { id: 'resumo', label: 'Resumo', icon: <FaFileAlt /> },
    { id: 'expositor', label: 'Expositor', icon: <FaBuilding /> },
    { id: 'itens', label: 'Itens', icon: <FaShoppingCart /> }
  ];

  // Função para mudar de aba
  const changeTab = (tab) => {
    setActiveTab(tab);
  };

  // Carregar dados do Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        if (isAdmin) {
          await Promise.all([
            fetchAllOrders(),
            fetchExpositores(),
            fetchEventos()
          ]);
        } else {
          await Promise.all([
            fetchOrders(),
            fetchEventos()
          ]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setError('Falha ao carregar os dados necessários.');
        setLoading(false);
      }
    };

    if (user?.id) {
      loadData();
    }

    // Adicionar estilos CSS para animações e melhorar a tabela
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(30px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .btn-submit:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }
      
      .status-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;
        padding: 0.4rem 0.8rem;
        border-radius: 20px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .action-button {
        padding: 0.75rem 1.5rem;
        border-radius: 50px;
        background-color: #212529;
        color: white;
        font-weight: 600;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        transition: all 0.3s ease;
        border: none;
        cursor: pointer;
      }

      .action-button:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }

      .view-button {
        padding: 0.6rem 1.2rem;
        border-radius: 50px;
        background-color: white;
        color: #495057;
        font-weight: 600;
        border: 1px solid #dee2e6;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        transition: all 0.3s ease;
        cursor: pointer;
      }

      .view-button:hover {
        background-color: #f8f9fa;
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      }

      .modal-close-btn:hover {
        background-color: #f8f9fa;
        color: #212529;
        transform: rotate(90deg);
      }
      
      .btn-cancel:hover {
        background-color: #f8f9fa;
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      }
      
      .btn-submit:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }

      .table-container {
        position: relative;
        overflow: auto;
        max-height: 400px;
        border-radius: 8px;
        border: 1px solid #dee2e6;
      }
      
      .tab:hover {
        background-color: ${colors.white};
        color: ${colors.primary};
      }

      /* Estilos para a posição relativa do ícone de busca */
      .position-relative .position-absolute {
        pointer-events: none;
      }

      @media (max-width: 768px) {
        .d-flex.justify-content-end {
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .d-flex.justify-content-end .btn {
          width: 100%;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [isAdmin, user]);

  // Calcular o sumário do pedido quando os itens são carregados
  useEffect(() => {
    if (!currentOrder?.items?.length) return;
    
    const summary = currentOrder.items.reduce((acc, item) => {
      const quantity = item.quantity || 1;
      const price = parseFloat(item.price) || 0;
      const originalPrice = parseFloat(item.originalPrice) || price;
      const discountAmount = parseFloat(item.discountAmount) || 0;
      const total = parseFloat(item.total) || (price * quantity);
      
      const originalItemTotal = originalPrice * quantity;
      const currentItemTotal = total;
      
      return {
        originalTotal: acc.originalTotal + originalItemTotal,
        finalTotal: acc.finalTotal + currentItemTotal,
        discountTotal: acc.discountTotal + (originalItemTotal - currentItemTotal),
        itemCount: acc.itemCount + 1,
        itemsWithDiscount: acc.itemsWithDiscount + (discountAmount > 0 ? 1 : 0)
      };
    }, {
      originalTotal: 0,
      finalTotal: 0,
      discountTotal: 0,
      itemCount: 0,
      itemsWithDiscount: 0
    });
    
    setOrderSummary(summary);
  }, [currentOrder?.items]);

  // Funções de fetch
  const fetchOrders = async () => {
    try {
      const { data: userProfile, error: profileError } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa, responsavel, email, telefone')
        .eq('usuario_id', user.id)
        .eq('ativo', true)
        .single();

      if (profileError) {
        throw new Error('Perfil de expositor não encontrado');
      }

      const { data, error } = await supabase
        .from('caex_pedidos')
        .select(`
          *,
          caex_expositores!inner(nome_empresa, responsavel, email, telefone),
          caex_eventos!inner(nome, data_inicio, data_fim)
        `)
        .eq('expositor_id', userProfile.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      const formattedOrders = data.map(order => ({
        id: order.id,
        exhibitorId: order.expositor_id,
        exhibitorName: order.caex_expositores.nome_empresa,
        eventId: order.evento_id,
        eventName: order.caex_eventos.nome,
        totalValue: order.valor_total,
        status: mapStatusFromDB(order.status),
        createdAt: order.created_at,
        notes: order.observacoes || '',
        contact: order.caex_expositores.responsavel,
        email: order.caex_expositores.email,
        phone: order.caex_expositores.telefone
      }));
      
      setOrders(formattedOrders);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      setError(`Não foi possível carregar a lista de pedidos: ${error.message}`);
    }
  };

  const fetchAllOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_pedidos')
        .select(`
          *,
          caex_expositores!inner(nome_empresa, responsavel, email, telefone),
          caex_eventos!inner(nome, data_inicio, data_fim)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      const formattedOrders = data.map(order => ({
        id: order.id,
        exhibitorId: order.expositor_id,
        exhibitorName: order.caex_expositores.nome_empresa,
        eventId: order.evento_id,
        eventName: order.caex_eventos.nome,
        totalValue: order.valor_total,
        status: mapStatusFromDB(order.status),
        createdAt: order.created_at,
        notes: order.observacoes || '',
        contact: order.caex_expositores.responsavel,
        email: order.caex_expositores.email,
        phone: order.caex_expositores.telefone
      }));
      
      setOrders(formattedOrders);
    } catch (error) {
      console.error('Erro ao carregar todos os pedidos:', error);
      setError(`Não foi possível carregar a lista de pedidos: ${error.message}`);
    }
  };

  const fetchExpositores = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa')
        .eq('ativo', true)
        .order('nome_empresa', { ascending: true });
      
      if (error) throw error;
      setExpositores(data);
    } catch (error) {
      console.error('Erro ao carregar expositores:', error);
    }
  };

  const fetchEventos = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome', { ascending: true });
      
      if (error) throw error;
      setEventos(data);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    }
  };

  const fetchOrderItems = async (orderId) => {
    try {
      const { data, error } = await supabase
        .from('caex_pedidos_itens')
        .select(`
          *,
          caex_produtos!inner(nome, descricao, categoria_id)
        `)
        .eq('pedido_id', orderId);
      
      if (error) throw error;

      const formattedItems = data.map(item => {
        const quantidade = parseInt(item.quantidade) || 1;
        const precoUnitario = parseFloat(item.preco_unitario) || 0;
        const precoTotal = parseFloat(item.preco_total) || 0;
        const precoOriginal = parseFloat(item.preco_original) || precoUnitario;
        const desconto = parseFloat(item.desconto) || 0;
        
        return {
          id: item.id,
          productId: item.produto_id,
          name: item.caex_produtos.nome,
          description: item.caex_produtos.descricao,
          category: item.caex_produtos.categoria_id,
          quantity: quantidade,
          price: precoUnitario,
          total: precoTotal,
          originalPrice: precoOriginal,
          discountAmount: desconto,
          discountType: item.tipo_desconto || 'value',
          hasDiscount: desconto > 0
        };
      });
      
      return formattedItems;
    } catch (error) {
      console.error('Erro ao carregar itens do pedido:', error);
      return [];
    }
  };

  const mapStatusFromDB = (dbStatus) => {
    const statusMap = {
      'pendente': 'Pendente',
      'aprovado': 'Aprovado',
      'rejeitado': 'Rejeitado'
    };
    return statusMap[dbStatus] || dbStatus;
  };

  const viewOrderDetails = async (order) => {
    const items = await fetchOrderItems(order.id);
    setCurrentOrder({...order, items});
    setActiveTab('resumo');
    setShowDetailsModal(true);
  };

  // Função para comprovante baseado na imagem (somente impressão)
  const handlePrintReceipt = (order) => {
    try {
      printImageBasedReceipt(order);
    } catch (error) {
      console.error('Erro ao imprimir comprovante:', error);
      alert('Erro ao gerar comprovante para impressão. Tente novamente.');
    }
  };

  // Funções auxiliares
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Calcular economia total
  const calculateTotalSavings = (items) => {
    return items.reduce((total, item) => {
      return total + (item.discountAmount * item.quantity);
    }, 0);
  };

  // Função para limpar todos os filtros
  const clearAllFilters = () => {
    setStatusFilter('Todos');
    setExpositorFilter('Todos');
    setEventoFilter('Todos');
    setDateFilter('Todos');
    setStartDate('');
    setEndDate('');
    setValueFilter('Todos');
    setSearchTerm('');
  };

  // Função para verificar se há filtros ativos
  const hasActiveFilters = () => {
    return statusFilter !== 'Todos' ||
           expositorFilter !== 'Todos' ||
           eventoFilter !== 'Todos' ||
           dateFilter !== 'Todos' ||
           startDate !== '' ||
           endDate !== '' ||
           valueFilter !== 'Todos' ||
           searchTerm.trim() !== '';
  };

  // Função para obter resumo dos filtros ativos
  const getActiveFiltersText = () => {
    const active = [];
    if (statusFilter !== 'Todos') active.push(`Status: ${statusFilter}`);
    if (expositorFilter !== 'Todos') {
      const expositor = expositores.find(e => e.id.toString() === expositorFilter);
      active.push(`Expositor: ${expositor?.nome_empresa || expositorFilter}`);
    }
    if (eventoFilter !== 'Todos') {
      const evento = eventos.find(e => e.id.toString() === eventoFilter);
      active.push(`Evento: ${evento?.nome || eventoFilter}`);
    }
    if (dateFilter !== 'Todos') active.push(`Período: ${dateFilter}`);
    if (startDate && endDate) active.push(`Data: ${startDate} até ${endDate}`);
    if (valueFilter !== 'Todos') active.push(`Valor: ${valueFilter}`);
    if (searchTerm.trim()) active.push(`Busca: "${searchTerm}"`);
    
    return active.join(' • ');
  };

  // Função melhorada para aplicar filtros
  const applyFilters = (orders) => {
    let filtered = orders;

    // Filtro por status
    if (statusFilter !== 'Todos') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filtro por expositor (apenas admin)
    if (isAdmin && expositorFilter !== 'Todos') {
      filtered = filtered.filter(order => order.exhibitorId === parseInt(expositorFilter));
    }

    // Filtro por evento
    if (eventoFilter !== 'Todos') {
      filtered = filtered.filter(order => order.eventId === parseInt(eventoFilter));
    }

    // Filtro por data
    if (dateFilter !== 'Todos') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        const orderDateOnly = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
        
        switch (dateFilter) {
          case 'Hoje':
            return orderDateOnly.getTime() === today.getTime();
          case 'Última semana':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return orderDateOnly >= weekAgo;
          case 'Último mês':
            const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
            return orderDateOnly >= monthAgo;
          case 'Últimos 3 meses':
            const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
            return orderDateOnly >= threeMonthsAgo;
          default:
            return true;
        }
      });
    }

    // Filtro por data personalizada
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Incluir o dia inteiro
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= start && orderDate <= end;
      });
    }

    // Filtro por valor
    if (valueFilter !== 'Todos') {
      filtered = filtered.filter(order => {
        const value = parseFloat(order.totalValue);
        switch (valueFilter) {
          case 'Até R$ 100':
            return value <= 100;
          case 'R$ 101 - R$ 500':
            return value > 100 && value <= 500;
          case 'R$ 501 - R$ 1000':
            return value > 500 && value <= 1000;
          case 'Acima de R$ 1000':
            return value > 1000;
          default:
            return true;
        }
      });
    }

    // Filtro por busca
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(order => 
        order.id.toString().includes(term) || 
        order.eventName.toLowerCase().includes(term) ||
        order.exhibitorName.toLowerCase().includes(term) ||
        order.contact?.toLowerCase().includes(term) ||
        order.notes?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'Aprovado':
        return {
          color: '#28a745',
          icon: <FaCheckCircle />,
          bgColor: '#28a74520'
        };
      case 'Rejeitado':
        return {
          color: '#dc3545',
          icon: <FaTimesCircle />,
          bgColor: '#dc354520'
        };
      case 'Pendente':
        return {
          color: '#ffc107',
          icon: <FaExclamationCircle />,
          bgColor: '#ffc10720'
        };
      default:
        return {
          color: '#6c757d',
          icon: null,
          bgColor: '#6c757d20'
        };
    }
  };

  // Renderizar conteúdo da aba ativa (mantido do original)
  const renderTabContent = () => {
    if (!currentOrder) return null;

    switch (activeTab) {
      case 'resumo':
        return (
          <div style={modalStyles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <h3 style={modalStyles.sectionTitle}>
                <FaFileAlt style={{color: colors.muted}} />
                Resumo do Pedido
              </h3>
              <span style={{ 
                ...modalStyles.statusBadge,
                backgroundColor: getStatusInfo(currentOrder.status).bgColor,
                color: getStatusInfo(currentOrder.status).color
              }}>
                {getStatusInfo(currentOrder.status).icon}
                {currentOrder.status}
              </span>
            </div>
            
            <div style={modalStyles.orderSummaryCard}>
              {isAdmin && (
                <div style={modalStyles.summaryRow}>
                  <span style={modalStyles.summaryLabel}>Expositor:</span>
                  <span style={modalStyles.summaryValue}>{currentOrder.exhibitorName}</span>
                </div>
              )}
              <div style={modalStyles.summaryRow}>
                <span style={modalStyles.summaryLabel}>Evento:</span>
                <span style={modalStyles.summaryValue}>{currentOrder.eventName}</span>
              </div>
              <div style={modalStyles.summaryRow}>
                <span style={modalStyles.summaryLabel}>Data do pedido:</span>
                <span style={modalStyles.summaryValue}>{formatDate(currentOrder.createdAt)}</span>
              </div>
              <div style={modalStyles.summaryRow}>
                <span style={modalStyles.summaryLabel}>Itens no pedido:</span>
                <span style={modalStyles.summaryValue}>{orderSummary.itemCount}</span>
              </div>
              {orderSummary.itemsWithDiscount > 0 && (
                <div style={modalStyles.summaryRow}>
                  <span style={modalStyles.summaryLabel}>Itens com desconto:</span>
                  <span style={modalStyles.summaryValue}>{orderSummary.itemsWithDiscount}</span>
                </div>
              )}
              {orderSummary.discountTotal > 0 && (
                <div style={modalStyles.summaryRow}>
                  <span style={modalStyles.summaryLabel}>Desconto total:</span>
                  <span style={{...modalStyles.summaryValue, ...modalStyles.discountTotal}}>
                    {formatCurrency(orderSummary.discountTotal)}
                  </span>
                </div>
              )}
              <div style={modalStyles.summaryTotal}>
                <div style={modalStyles.summaryRow}>
                  <span>Valor total do pedido:</span>
                  {orderSummary.discountTotal > 0 ? (
                    <div style={{textAlign: 'right'}}>
                      <div style={{fontSize: '0.85rem', color: colors.muted, textDecoration: 'line-through'}}>
                        {formatCurrency(orderSummary.originalTotal)}
                      </div>
                      <div>{formatCurrency(orderSummary.finalTotal)}</div>
                    </div>
                  ) : (
                    <span>{formatCurrency(orderSummary.finalTotal)}</span>
                  )}
                </div>
              </div>
            </div>

            {currentOrder.notes && (
              <div>
                <h4 style={{...modalStyles.sectionTitle, fontSize: '1rem', marginBottom: '0.5rem'}}>
                  <FaInfoCircle style={{color: colors.muted}} />
                  Observações
                </h4>
                <p style={{fontSize: '0.9rem', color: colors.secondary, lineHeight: '1.4'}}>
                  {currentOrder.notes}
                </p>
              </div>
            )}
          </div>
        );

      case 'expositor':
        return (
          <div style={modalStyles.section}>
            <h3 style={modalStyles.sectionTitle}>
              <FaBuilding style={{color: colors.muted}} />
              Informações do Expositor
            </h3>
            <div style={modalStyles.grid}>
              <div>
                <p style={modalStyles.infoLabel}>Empresa</p>
                <p style={modalStyles.infoValue}>{currentOrder.exhibitorName}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Responsável</p>
                <p style={modalStyles.infoValue}>{currentOrder.contact || 'Não informado'}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Email</p>
                <p style={modalStyles.infoValue}>{currentOrder.email || 'Não informado'}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Telefone</p>
                <p style={modalStyles.infoValue}>{currentOrder.phone || 'Não informado'}</p>
              </div>
            </div>
          </div>
        );

      case 'itens':
        return (
          <div style={modalStyles.section}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3 style={modalStyles.sectionTitle}>
                <FaShoppingCart style={{color: colors.muted}} />
                Itens do Pedido
              </h3>
            </div>
            
            <div className="table-container">
              <table style={modalStyles.itemTable}>
                <thead>
                  <tr>
                    <th style={modalStyles.itemTableHeader}>Item</th>
                    <th style={{...modalStyles.itemTableHeader, width: '80px', textAlign: 'center'}}>Qtd</th>
                    <th style={{...modalStyles.itemTableHeader, width: '130px', textAlign: 'right'}}>Preço Unit.</th>
                    <th style={{...modalStyles.itemTableHeader, width: '130px', textAlign: 'right'}}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {currentOrder.items && currentOrder.items.length > 0 ? (
                    currentOrder.items.map((item) => (
                      <tr key={item.id}>
                        <td style={modalStyles.itemTableCell}>
                          <div>
                            <div style={{fontWeight: '500'}}>{item.name}</div>
                            {item.description && <small style={{color: colors.muted}}>{item.description}</small>}
                          </div>
                        </td>
                        <td style={{...modalStyles.itemTableCell, textAlign: 'center'}}>{item.quantity}</td>
                        <td style={{...modalStyles.itemTableCell, textAlign: 'right'}}>
                          <div style={modalStyles.priceContainer}>
                            {item.hasDiscount && (
                              <span style={modalStyles.originalPrice}>
                                {formatCurrency(item.originalPrice)}
                              </span>
                            )}
                            <span style={item.hasDiscount ? modalStyles.discountedPrice : modalStyles.regularPrice}>
                              {formatCurrency(item.price)}
                            </span>
                            {item.hasDiscount && (
                              <span style={modalStyles.discountBadge}>
                                <FaTag size={10} />
                                {formatCurrency(item.discountAmount)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{...modalStyles.itemTableCell, textAlign: 'right'}}>
                          {formatCurrency(item.total)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td 
                        colSpan={4}
                        style={{...modalStyles.itemTableCell, textAlign: 'center', padding: '2rem'}}
                      >
                        <div style={{color: colors.muted, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'}}>
                          <FaInfoCircle size={32} />
                          <div>
                            <p style={{fontWeight: '500', marginBottom: '0.5rem'}}>Nenhum item encontrado</p>
                            <p style={{fontSize: '0.9rem'}}>Este pedido não possui itens registrados.</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {currentOrder.items && currentOrder.items.length > 0 && orderSummary.discountTotal > 0 && (
                    <tr style={{backgroundColor: colors.successLight}}>
                      <td 
                        colSpan={3} 
                        style={{
                          ...modalStyles.itemTableCell, 
                          textAlign: 'right',
                          color: colors.success,
                          fontStyle: 'italic',
                          fontWeight: '600'
                        }}
                      >
                        <em>Economia total:</em>
                      </td>
                      <td 
                        style={{
                          ...modalStyles.itemTableCell, 
                          textAlign: 'right',
                          color: colors.success,
                          fontWeight: '700'
                        }}
                      >
                        <em>{formatCurrency(orderSummary.discountTotal)}</em>
                      </td>
                    </tr>
                  )}
                  
                  {currentOrder.items && currentOrder.items.length > 0 && (
                    <tr style={{backgroundColor: colors.light, borderTop: `2px solid ${colors.border}`}}>
                      <td 
                        colSpan={3} 
                        style={{
                          ...modalStyles.itemTableCell, 
                          textAlign: 'right',
                          fontWeight: '700',
                          fontSize: '1.125rem',
                          color: colors.secondary
                        }}
                      >
                        <strong>Total:</strong>
                      </td>
                      <td 
                        style={{
                          ...modalStyles.itemTableCell, 
                          textAlign: 'right',
                          fontWeight: '700',
                          fontSize: '1.25rem',
                          color: colors.dark
                        }}
                      >
                        <strong>{formatCurrency(orderSummary.finalTotal)}</strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const filteredOrders = applyFilters(orders);

  // Render loading state
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaClipboardList className="dashboard-title-icon" />
                {isAdmin ? 'Todos os Pedidos' : 'Meus Pedidos'}
              </h1>
              <p className="dashboard-subtitle">
                {isAdmin 
                  ? 'Visualize e gerencie todos os pedidos dos expositores' 
                  : 'Visualize e acompanhe o status dos seus pedidos'}
              </p>
            </div>
          </div>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3">Carregando pedidos...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaClipboardList className="dashboard-title-icon" />
                {isAdmin ? 'Todos os Pedidos' : 'Meus Pedidos'}
              </h1>
              <p className="dashboard-subtitle">
                {isAdmin 
                  ? 'Visualize e gerencie todos os pedidos dos expositores' 
                  : 'Visualize e acompanhe o status dos seus pedidos'}
              </p>
            </div>
          </div>
          <div className="alert alert-danger">
            <h5>Erro ao carregar dados</h5>
            <p>{error}</p>
            <button 
              className="btn btn-outline-primary mt-3"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaClipboardList className="dashboard-title-icon" />
              {isAdmin ? 'Todos os Pedidos' : 'Meus Pedidos'}
            </h1>
            <p className="dashboard-subtitle">
              {isAdmin 
                ? 'Visualize e gerencie todos os pedidos dos expositores' 
                : 'Visualize e acompanhe o status dos seus pedidos'}
            </p>
          </div>
          
          {!isAdmin && (
            <div>
              <Link to="/catalog" className="action-button">
                <FaShoppingCart />
                Fazer Novo Pedido
              </Link>
            </div>
          )}
        </div>

        {/* Botões de ação */}
        <div className="d-flex justify-content-end mb-4">
          <button 
            className="btn btn-outline-secondary me-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? <FaTimes className="me-2" /> : <FaFilter className="me-2" />}
            {showFilters ? 'Ocultar Filtros' : 'Filtros'}
          </button>
          
        </div>

        {/* Área de filtros */}
        {showFilters && (
          <div className="modern-table-card mb-4">
            <div className="modern-card-body">
              {/* Resumo dos Filtros Ativos */}
              {hasActiveFilters() && (
                <div className="alert alert-info mb-3">
                  <FaInfoCircle className="me-2" />
                  <strong>Filtros ativos:</strong> {getActiveFiltersText()}
                </div>
              )}

              {/* Filtros Básicos */}
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Status do Pedido</label>
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="Todos">Todos os status</option>
                      <option value="Pendente">Pendente</option>
                      <option value="Aprovado">Aprovado</option>
                      <option value="Rejeitado">Rejeitado</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Evento</label>
                    <select
                      className="form-select"
                      value={eventoFilter}
                      onChange={(e) => setEventoFilter(e.target.value)}
                    >
                      <option value="Todos">Todos os eventos</option>
                      {eventos.map((evento) => (
                        <option key={evento.id} value={evento.id}>
                          {evento.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {isAdmin && (
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Expositor</label>
                      <select
                        className="form-select"
                        value={expositorFilter}
                        onChange={(e) => setExpositorFilter(e.target.value)}
                      >
                        <option value="Todos">Todos os expositores</option>
                        {expositores.map((expositor) => (
                          <option key={expositor.id} value={expositor.id}>
                            {expositor.nome_empresa}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className={`col-md-${isAdmin ? '3' : '6'}`}>
                  <div className="mb-3">
                    <label className="form-label">Buscar</label>
                    <div className="position-relative">
                      <input 
                        type="text" 
                        className="form-control"
                        placeholder="Nº pedido, evento, expositor..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros Avançados */}
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Período</label>
                    <select
                      className="form-select"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                    >
                      <option value="Todos">Todos os períodos</option>
                      <option value="Hoje">Hoje</option>
                      <option value="Última semana">Última semana</option>
                      <option value="Último mês">Último mês</option>
                      <option value="Últimos 3 meses">Últimos 3 meses</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Data Início</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Data Fim</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Faixa de Valor</label>
                    <select
                      className="form-select"
                      value={valueFilter}
                      onChange={(e) => setValueFilter(e.target.value)}
                    >
                      <option value="Todos">Todos os valores</option>
                      <option value="Até R$ 100">Até R$ 100</option>
                      <option value="R$ 101 - R$ 500">R$ 101 - R$ 500</option>
                      <option value="R$ 501 - R$ 1000">R$ 501 - R$ 1000</option>
                      <option value="Acima de R$ 1000">Acima de R$ 1000</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Botão para limpar filtros */}
              <div className="row">
                <div className="col-12">
                  <div className="d-flex justify-content-end">
                    {hasActiveFilters() && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={clearAllFilters}
                      >
                        <FaTimes className="me-2" />
                        Limpar Filtros
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabela de pedidos */}
        <div className="modern-table-card" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <div className="modern-card-header">
            <h5 className="modern-card-title">
              <FaClipboardList className="card-title-icon" />
              {isAdmin ? 'Lista de Pedidos' : 'Seus Pedidos'} {filteredOrders.length > 0 && `(${filteredOrders.length})`}
            </h5>
          </div>
          <div className="modern-card-body">
            <div className="modern-table-container">
              {filteredOrders.length === 0 ? (
                <div className="empty-state-container">
                  <FaShoppingCart className="empty-state-icon" />
                  <h3 className="empty-state-title">
                    {hasActiveFilters() ? 'Nenhum pedido encontrado' : (isAdmin ? 'Nenhum pedido encontrado' : 'Você ainda não tem pedidos')}
                  </h3>
                  <p className="empty-state-message">
                    {hasActiveFilters() 
                      ? 'Não foram encontrados pedidos com os filtros aplicados. Tente ajustar os filtros.'
                      : (isAdmin 
                        ? 'Não foram encontrados pedidos no sistema.'
                        : 'Você ainda não realizou nenhum pedido. Clique no botão abaixo para fazer seu primeiro pedido.')}
                  </p>
                  {hasActiveFilters() ? (
                    <button onClick={clearAllFilters} className="action-button">
                      <FaTimes />
                      Limpar Filtros
                    </button>
                  ) : (!isAdmin && (
                    <Link to="/catalog" className="action-button">
                      <FaShoppingCart />
                      Fazer Pedido
                    </Link>
                  ))}
                </div>
              ) : (
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Nº Pedido</th>
                      {isAdmin && <th>Expositor</th>}
                      <th>Evento</th>
                      <th>Data</th>
                      <th>Valor Total</th>
                      <th>Status</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => {
                      const statusInfo = getStatusInfo(order.status);
                      return (
                        <tr key={order.id}>
                          <td className="order-id">#{order.id}</td>
                          {isAdmin && <td>{order.exhibitorName}</td>}
                          <td className="event-name">{order.eventName}</td>
                          <td className="order-date">{formatDate(order.createdAt)}</td>
                          <td className="order-value">{formatCurrency(order.totalValue)}</td>
                          <td>
                            <span className="status-badge" style={{ 
                              backgroundColor: statusInfo.bgColor,
                              color: statusInfo.color,
                            }}>
                              {statusInfo.icon}
                              {order.status}
                            </span>
                          </td>
                          <td className="text-right">
                            <button 
                              onClick={() => viewOrderDetails(order)}
                              className="view-button"
                            >
                              <FaEye />
                              Ver detalhes
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Modal de detalhes do pedido com sistema de abas */}
        {showDetailsModal && currentOrder && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  <FaClipboardList style={{color: colors.muted}} />
                  Detalhes do Pedido #{currentOrder.id}
                </h2>
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              {/* Sistema de Abas */}
              <div style={modalStyles.tabsContainer}>
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    style={{
                      ...modalStyles.tab,
                      ...(activeTab === tab.id ? modalStyles.activeTab : {})
                    }}
                    className="tab"
                    onClick={() => changeTab(tab.id)}
                  >
                    {tab.icon}
                    {tab.label}
                  </div>
                ))}
              </div>
              
              <div style={modalStyles.tabContent} className="modal-scrollable-body">
                {renderTabContent()}
              </div>
              
              <div style={modalStyles.footer}>
                {isAdmin && (
                  <Link
                    to={`/admin/orders?pedido=${currentOrder.id}`}
                    className="btn-submit"
                    style={{
                      ...modalStyles.footerButton,
                      backgroundColor: '#212529',
                      marginRight: '0.75rem',
                      textDecoration: 'none'
                    }}
                  >
                    <FaClipboardCheck />
                    Ir para Aprovação
                  </Link>
                )}
                
                {!isAdmin && currentOrder.status === 'Aprovado' && (
                  <button
                    type="button"
                    onClick={() => handlePrintReceipt(currentOrder)}
                    className="btn-submit"
                    style={{
                      ...modalStyles.footerButton,
                      backgroundColor: '#212529',
                      color: 'white',
                      marginRight: '0.75rem'
                    }}
                  >
                    <FaPrint />
                    Imprimir
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  className="btn-cancel"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersList;